import React, { useState } from 'react';
import { Routes, Route, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TeacherManagement from '../components/Admin/TeacherManagement';
import ClassManagement from '../components/Admin/ClassManagement';
import SubjectManagement from '../components/Admin/SubjectManagement';
import ChapterManagement from '../components/Admin/ChapterManagement';
import ContentManagement from '../components/Admin/ContentManagement';
import MCQManagement from '../components/Admin/MCQManagement';
import ResponseManagement from '../components/Admin/ResponseManagement';
import {
  RiDashboardLine, RiUserLine, RiBookOpenLine, RiBookLine,
  RiFileListLine, RiFolderLine, RiQuestionLine, RiBarChartLine,
  RiLogoutBoxLine, RiMenuLine, RiCloseLine, RiShieldUserLine,
} from 'react-icons/ri';

const menuItems = [
  { id: 'dashboard', label: 'Dashboard',         icon: RiDashboardLine, path: '/admin' },
  { id: 'teachers',  label: 'Teachers',           icon: RiUserLine,       path: '/admin/teachers' },
  { id: 'classes',   label: 'Classes',            icon: RiBookOpenLine,   path: '/admin/classes' },
  { id: 'subjects',  label: 'Subjects',           icon: RiBookLine,       path: '/admin/subjects' },
  { id: 'chapters',  label: 'Chapters',           icon: RiFileListLine,   path: '/admin/chapters' },
  { id: 'content',   label: 'Content',            icon: RiFolderLine,     path: '/admin/content' },
  { id: 'mcq',       label: 'MCQ Tests',          icon: RiQuestionLine,   path: '/admin/mcq' },
  { id: 'responses', label: 'Student Responses',  icon: RiBarChartLine,   path: '/admin/responses' },
];

const AdminDashboard = () => {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  const active = menuItems.find(m => {
    if (m.path === '/admin') return location.pathname === '/admin' || location.pathname === '/admin/';
    return location.pathname.startsWith(m.path);
  })?.id || 'dashboard';

  const Sidebar = ({ mobile = false }) => (
    <aside className={`flex flex-col h-full bg-slate-900 ${mobile ? 'w-full' : 'w-60'}`}>
      {/* Brand */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-slate-800">
        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
          <RiBookOpenLine className="w-4 h-4 text-white" />
        </div>
        <div>
          <p className="text-white font-bold text-sm leading-tight">Demo Publication</p>
          <p className="text-slate-500 text-xs">Admin Panel</p>
        </div>
        {mobile && (
          <button onClick={() => setSidebarOpen(false)} className="ml-auto text-slate-400 hover:text-white">
            <RiCloseLine className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {menuItems.map(({ id, label, icon: Icon, path }) => (
          <Link
            key={id}
            to={path}
            onClick={() => setSidebarOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
              active === id
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
            }`}
          >
            <Icon className="w-4 h-4 flex-shrink-0" />
            {label}
          </Link>
        ))}
      </nav>

      {/* User */}
      <div className="px-3 py-4 border-t border-slate-800">
        <div className="flex items-center gap-3 px-3 py-2 mb-2">
          <div className="w-7 h-7 bg-blue-700 rounded-full flex items-center justify-center flex-shrink-0">
            <RiShieldUserLine className="w-3.5 h-3.5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-slate-300 text-xs font-medium truncate">{auth.user?.adminId}</p>
            <p className="text-slate-600 text-xs">Administrator</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-400 hover:bg-red-950 hover:text-red-400 transition-all duration-150"
        >
          <RiLogoutBoxLine className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen bg-slate-100 overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden md:flex flex-col w-60 flex-shrink-0 border-r border-slate-200 shadow-sm">
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="relative w-64 flex flex-col shadow-2xl">
            <Sidebar mobile />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Topbar */}
        <header className="bg-white border-b border-slate-200 px-5 py-3.5 flex items-center justify-between flex-shrink-0 shadow-sm">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <RiMenuLine className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-base font-semibold text-slate-800">
                {menuItems.find(m => m.id === active)?.label || 'Dashboard'}
              </h1>
              <p className="text-xs text-slate-400 hidden sm:block">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>
        </header>

        {/* Page */}
        <main className="flex-1 overflow-auto">
          <Routes>
            <Route path="/"          element={<AdminDashboardHome />} />
            <Route path="/teachers"  element={<TeacherManagement />} />
            <Route path="/classes"   element={<ClassManagement />} />
            <Route path="/subjects"  element={<SubjectManagement />} />
            <Route path="/chapters"  element={<ChapterManagement />} />
            <Route path="/content"   element={<ContentManagement />} />
            <Route path="/mcq"       element={<MCQManagement />} />
            <Route path="/responses" element={<ResponseManagement />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

const statCards = [
  { label: 'Teachers',          color: 'blue',    icon: RiUserLine },
  { label: 'Classes',           color: 'emerald', icon: RiBookOpenLine },
  { label: 'MCQ Tests',         color: 'violet',  icon: RiQuestionLine },
  { label: 'Student Responses', color: 'amber',   icon: RiBarChartLine },
];

const colorMap = {
  blue:    { bg: 'bg-blue-50',    icon: 'bg-blue-100 text-blue-600',    val: 'text-blue-600' },
  emerald: { bg: 'bg-emerald-50', icon: 'bg-emerald-100 text-emerald-600', val: 'text-emerald-600' },
  violet:  { bg: 'bg-violet-50',  icon: 'bg-violet-100 text-violet-600', val: 'text-violet-600' },
  amber:   { bg: 'bg-amber-50',   icon: 'bg-amber-100 text-amber-600',  val: 'text-amber-600' },
};

const AdminDashboardHome = () => (
  <div className="p-6 max-w-5xl mx-auto animate-fade-in-up">
    {/* Stat cards */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 stagger">
      {statCards.map(({ label, color, icon: Icon }) => {
        const c = colorMap[color];
        return (
          <div key={label} className={`${c.bg} border border-slate-200 rounded-xl p-5 animate-fade-in-up`}>
            <div className={`w-9 h-9 rounded-lg ${c.icon} flex items-center justify-center mb-3`}>
              <Icon className="w-4 h-4" />
            </div>
            <p className={`text-2xl font-bold ${c.val} leading-none`}>—</p>
            <p className="text-xs text-slate-500 font-medium mt-1">{label}</p>
          </div>
        );
      })}
    </div>

    {/* Welcome */}
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <h3 className="text-base font-semibold text-slate-800 mb-1">Welcome to Admin Dashboard</h3>
      <p className="text-sm text-slate-500 mb-4">
        Use the sidebar to manage your institution's academic structure, content, and tests.
      </p>
      <ul className="space-y-2">
        {[
          'Create and manage teacher accounts',
          'Organise academic structure — Classes, Subjects, Chapters',
          'Upload educational content (PDFs, videos, slides)',
          'Create MCQ tests and generate shareable links',
          'View and analyse student responses',
        ].map(item => (
          <li key={item} className="flex items-center gap-2 text-sm text-slate-600">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

export default AdminDashboard;
